/**
 * Inhalte des geführten Rundgangs.
 *
 * Bewusst routen-basiert statt an CSS-Selektoren aufgehängt: ein Rundgang, der auf
 * konkrete Elemente zeigt, bricht bei der nächsten Layout-Änderung still — und diese
 * Oberfläche ändert sich wöchentlich. Der Rundgang navigiert stattdessen zur echten
 * Seite, die Erklärung liegt daneben. Was der Nutzer sieht, sind seine eigenen Daten,
 * nicht ein Demo-Screenshot, der beim nächsten Release nicht mehr stimmt.
 */

export interface TourStep {
  id: string;
  /** Ziel-Route — der Rundgang navigiert dorthin, bevor der Text erscheint. */
  path: string;
  title: string;
  /** Worum es auf dieser Seite geht — zwei bis vier Sätze, kein Handbuch. */
  body: string;
  /** Konkrete Handgriffe in der Reihenfolge, in der man sie braucht. */
  steps?: string[];
  /** Häufige Stolperstelle oder Verwechslungsgefahr. */
  caution?: string;
  /** Wann man diese Funktion NICHT braucht — die häufigste offene Frage. */
  notFor?: string;
  /** Nur für Admins sichtbar. */
  adminOnly?: boolean;
  /** Bereich des Kundenportals, den die Station voraussetzt (PortalModuleId). */
  module?: string;
  /**
   * Bereich ist im Menü sichtbar, aber noch nicht freigeschaltet.
   *
   * Solche Stationen bleiben bewusst im Rundgang — die Seite steht ja im Menü und
   * der Nutzer fragt sich sonst, warum dort nichts passiert. Sie werden aber klar
   * als „noch nicht verfügbar" ausgewiesen: eine Anleitung, die Funktionen
   * verspricht, die es nicht gibt, verliert ihren Wert für alles Übrige mit.
   */
  comingSoon?: boolean;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'start',
    path: '/',
    title: 'Dein Startpunkt',
    body:
      'Das Dashboard zeigt, was heute ansteht: offene Tickets, Termine, Erinnerungen. ' +
      'Links in der Navigation liegen alle Bereiche — was du oft brauchst, kannst du dir ' +
      'über den Stern als Favorit nach oben holen. Mit Strg+K öffnest du überall die Suche.',
    steps: [
      'Über den Stern neben einem Menüpunkt legst du Favoriten an.',
      'Strg+K öffnet die Schnellsuche über alle Bereiche.',
    ],
  },
  {
    id: 'tickets',
    path: '/tickets',
    title: 'Tickets — drei Sichten auf dieselben Daten',
    body:
      'Liste, Disposition und Kanban zeigen denselben Bestand, nur anders sortiert. ' +
      'Die Liste ist der Alltag, Kanban eignet sich für den Überblick nach Status, ' +
      'Disposition für die Planung. Die Reiter darunter grenzen ein: deine Tickets, ' +
      'die des Teams, oder alles ohne Zuweisung.',
    steps: [
      'Reiter „Meine Tickets" — woran du gerade arbeitest.',
      'Reiter „Ohne Zuweisung" — der offene Pool, aus dem du dir etwas nehmen kannst.',
      'Ein Klick auf ein Ticket öffnet rechts das Cockpit mit allen Details.',
    ],
    notFor:
      'Die Disposition brauchst du als Techniker selten — sie ist für die Einsatzplanung gedacht.',
  },
  {
    id: 'ticket-assign',
    path: '/tickets?tab=unassigned',
    title: 'Ein Ticket übernehmen oder abgeben',
    body:
      'Du kannst dir jedes Ticket selbst nehmen — dafür gibt es im Cockpit „Ticket übernehmen". ' +
      'Einem anderen zuweisen kannst du es nicht direkt; dafür gibt es die Übergabe. ' +
      'Der Empfänger bekommt eine Nachricht und bestätigt oder lehnt ab. So bekommt niemand ' +
      'unbemerkt Arbeit zugeschoben.',
    steps: [
      'Ticket öffnen → „Ticket übernehmen" nimmt es auf dich.',
      'Für die Abgabe an eine andere Person: „Übergeben" und Empfänger wählen.',
      'Unter „Übergaben" siehst du, was du verteilt hast und wer angenommen hat.',
    ],
    caution:
      'Admins und Teamleitung dürfen direkt zuweisen — bei der Teamleitung mit kurzer Begründung, ' +
      'die als interner Kommentar im Ticket landet.',
  },
  {
    id: 'reminders-handovers',
    path: '/reminders',
    title: 'Aufgaben, Erinnerungen und offene Übergaben',
    body:
      'Zwei persönliche Merkzettel. Unter „Aufgaben & Erinnerungen" stehen deine eigenen ' +
      'Wiedervorlagen — Rückruf am Dienstag, Teil bestellt und wartet — und die Aufgaben, die dir ' +
      'Geschäftsführung, Büro oder Teamleitung gegeben haben. ' +
      'Unter „Übergaben" siehst du, was du an andere abgegeben hast und ob es angenommen wurde.',
    steps: [
      'Eine Erinnerung legst du auf der Seite oder im Ticket-Cockpit am Vorgang an (Glocke).',
      'Übergaben zeigt beide Richtungen: von dir verteilt und an dich gegeben.',
    ],
    caution:
      'Eine abgelehnte Übergabe fällt an dich zurück — deshalb lohnt der gelegentliche Blick ' +
      'auf die Liste.',
  },
  {
    id: 'ticket-cockpit',
    path: '/tickets?tab=mine',
    title: 'Das Ticket-Cockpit',
    body:
      'Öffne eines deiner Tickets. Rechts findest du alles zum Vorgang: Verlauf, Kommentare, ' +
      'gebuchte Zeiten, Anhänge und die KIAN-Analyse. Hier arbeitest du im Alltag.',
    steps: [
      'Aufklappbare Abschnitte: Beschreibung, Kommentare, Verlauf, Anhänge, ' +
        'E-Mail-Archiv, gebuchte Zeiten.',
      '„Zeit buchen" — für Arbeit an genau diesem Ticket.',
      'Kommentar schreiben: Screenshots mit Strg+V direkt einfügen, Kolleginnen ' +
        'und Kollegen per @ erwähnen.',
      '„Kunde zuordnen" bei Tickets ohne Firma — KIAN schlägt auf Wunsch einen vor.',
      '„Absender als lokalen Kontakt speichern" ist dabei bewusst standardmäßig AUS: nur ' +
        'einschalten, wenn der Absender eine echte Person beim Kunden ist — Automatik-Adressen ' +
        '(noreply@, Systemmails) gehören nicht ins Kontaktbuch.',
      '„Als Zuordnungsregel speichern": bei wiederkehrenden Automatik-Mails (z. B. Sophos-Reports) ' +
        'Absender, Empfänger und/oder Betreff-Teil als Regel hinterlegen — künftige Tickets ordnet ' +
        'das System dann ohne KIAN automatisch dem Kunden zu.',
      'Nach dem Buchen bleibt das Ergebnis als Meldung stehen — grün heißt gebucht.',
    ],
    caution:
      'Wenn eine Buchung fehlschlägt, steht der Grund im Klartext da. Bitte melden statt erneut ' +
      'buchen — sonst entstehen Doppelbuchungen.',
  },
  {
    id: 'triage',
    path: '/admin/callers',
    title: 'Anrufer & Rückrufe — wen muss ich heute zurückrufen?',
    body:
      'Unter „Anrufer & Rückrufe" liegt vorne die Rückrufliste: jeder eingehende Anruf steht dort ' +
      'als Aufgabe, mit Dringlichkeit, wie oft die Person schon angerufen hat, der Nachricht vom ' +
      'Telefonassistenten und den offenen Tickets der Firma. Der zweite Reiter „Zuordnung" ist die ' +
      'Aufräumarbeit dahinter: Nummern, die zu keinem Kontakt passen. Daneben gibt es „Tickets ohne ' +
      'Kunde" (Mails ohne zuordenbaren Absender) und „AP-Vorschläge" (neue Ansprechpartner, die ' +
      'KIAN aus Mailverkehr erkannt hat).',
    steps: [
      'Rückrufe: Eintrag anklicken, „Übernehmen" — dann sieht jeder, dass sich jemand kümmert.',
      'Nach dem Telefonat Ergebnis eintragen und auf „Erledigt" setzen. Ruft die Person erneut an, ' +
        'geht die Aufgabe automatisch wieder auf.',
      'Wiedervorlage setzen, wenn niemand erreichbar war — „Dienstag nochmal versuchen".',
      'Zuordnung: unbekannte Nummer einem Kontakt zuordnen oder neu anlegen.',
      'Tickets ohne Kunde: Firma zuweisen — danach greifen Verlauf und Auswertungen.',
      'AP-Vorschläge: bestätigen oder verwerfen.',
    ],
    caution:
      'Die rote Zahl am Menüpunkt zählt offene Rückrufe. Bleibt sie stehen, wartet jemand ' +
      'tatsächlich auf einen Anruf — die Dringlichkeit steigt automatisch mit jedem weiteren ' +
      'Anrufversuch.',
  },
  {
    id: 'appointments',
    path: '/appointments',
    title: 'Terminplanung',
    body:
      'Deine Einsatztermine mit Kalenderanbindung. Was hier steht, ist die verbindliche Planung — ' +
      'der Ausgangspunkt für den Service-Report am Einsatztag.',
    notFor:
      'Die Ticket-Disposition ist etwas anderes: dort wird verteilt, wer was macht. Hier steht, ' +
      'wann es stattfindet.',
  },
  {
    id: 'time-booking',
    path: '/time-booking',
    title: 'Zeitbuchung ohne Ticket',
    body:
      'Diese Seite ist für Arbeit, zu der es noch kein Ticket gibt — der schnelle Anruf, ' +
      'die kurze Fernwartung. Das Portal legt das Ticket an, bucht die Zeit und schließt es ' +
      'wieder. Du wählst nur Kunde, Dauer und was du gemacht hast.',
    notFor:
      'Gibt es bereits ein Ticket, buche die Zeit dort im Cockpit — sonst entsteht ein zweites.',
  },
  {
    id: 'service-report',
    path: '/service-report',
    title: 'Service-Report — der Einsatz vor Ort',
    body:
      'Für Arbeit beim Kunden vor Ort. Der Bericht führt dich durch Anfahrt, Aufgabe, Zeiten, ' +
      'Material, Fotos und Abnahme. Am Ende unterschreibt der Kunde, und der Bericht geht raus. ' +
      'Die gefahrenen Kilometer landen automatisch in deiner Fahrten-Übersicht.',
    steps: [
      'Ticket auswählen — der Kunde wird daraus übernommen.',
      'Anfahrt erfassen; die Strecke wird berechnet, sonst km von Hand eintragen.',
      'Zeiten und Material dokumentieren, Fotos anhängen.',
      'Abnahme: Unterschrift oder begründete Verweigerung.',
    ],
    caution:
      'Der Entwurf lässt sich jederzeit parken und später fortsetzen — du musst den Einsatz ' +
      'nicht am Stück erfassen.',
  },
  {
    id: 'delivery-report',
    path: '/delivery-report',
    title: 'Lieferschein — Material übergeben',
    body:
      'Wenn du Ware zum Kunden bringst und übergibst, ohne dass es ein Service-Einsatz ist. ' +
      'Kürzer als der Service-Report: Ticket, Anfahrt, Artikel, Abnahme, Rückfahrt.',
    notFor:
      'Hast du vor Ort auch gearbeitet, nimm den Service-Report — dort ist das Material ein ' +
      'eigener Schritt. Zwei Belege für denselben Einsatz brauchst du nicht.',
  },
  {
    id: 'trips',
    path: '/my-trips',
    title: 'Fahrten',
    body:
      'Hier siehst du deine Fahrten und die anerkannten Kilometer. Fahrten aus Service-Reports ' +
      'und Lieferscheinen stehen automatisch drin. Alles andere — Bürofahrt, Schulung, ' +
      'Besorgung — trägst du manuell nach.',
    caution:
      'Manuelle Fahrten werden nicht automatisch anerkannt; die Teamleitung prüft sie. ' +
      'Beschreibe deshalb kurz den Zweck.',
  },
  {
    id: 'customers',
    path: '/customer-hub',
    title: 'Alles zum Kunden an einem Ort',
    body:
      'Der Kunden-Hub bündelt, was zu einer Firma gehört: Angebote, Garantien, Bestellungen und ' +
      'Material im Zusammenhang. Daneben liegen drei weitere Nachschlagewerke — Infrastruktur, ' +
      'Wartungskunden und das Kontaktverzeichnis.',
    steps: [
      'Infrastruktur: welche Geräte, Netzwerke und Arbeitsplätze beim Kunden stehen.',
      'Wartungskunden: wer einen Vertrag hat und was er umfasst.',
      'Kontaktverzeichnis: Ansprechpartner mit Rufnummern über alle Kunden hinweg.',
    ],
    caution:
      'Vor einem Einsatz lohnt der Blick in die Infrastruktur — Modell, Standort und Seriennummer ' +
      'stehen meist schon dort, statt sie vor Ort zu suchen.',
  },
  {
    id: 'material',
    path: '/technician-requests',
    title: 'Materialanfragen',
    body:
      'Wenn du etwas brauchst — Werkzeug, Ersatzteil, Gerät. Der Schalter „Interne Anfrage" ' +
      'entscheidet, worum es geht: eingeschaltet heißt Beschaffung für die Firma, ausgeschaltet ' +
      'heißt, du bekommst es selbst und es wird über Gehalt oder Stunden verrechnet.',
    steps: [
      'Titel, Kategorie, Beschreibung — Betrag nur, wenn du ihn kennst.',
      'Kennst du ihn nicht, schätzt KIAN und kennzeichnet das als Schätzung.',
      'Über „Verlauf" stellst du Rückfragen und hängst Screenshots an.',
    ],
    caution:
      'Jede Anfrage wird von Hand freigegeben. Der Monatsrichtwert von 100 € ist nur ein Hinweis ' +
      'für die freigebende Person, keine automatische Genehmigung.',
  },
  {
    id: 'inventory',
    path: '/inventory',
    title: 'Lager',
    body:
      'Die Bestände kommen aus Weclapp und sind hier nur zum Lesen. Ändern tust du sie über die ' +
      'Vorgänge: Entnahme, wenn du etwas mitnimmst; Umlagerung zwischen Lagerplätzen; Rückgabe, ' +
      'wenn etwas zurückkommt. Auf deinem eigenen Lagerplatz findest du diese Aktionen oben rechts.',
    steps: [
      'Bestandssuche: wo liegt ein Artikel gerade, über alle Lager hinweg.',
      'Warenannahme: gelieferte Ware einbuchen, mit Foto bei Schäden.',
      'SNR-Lebenslauf: wo ein Gerät mit dieser Seriennummer überall war.',
    ],
    notFor:
      'Bestandswert, Unterbestand und Inventur brauchst du im Alltag nicht — die sind fürs Backoffice.',
  },
  {
    id: 'archive',
    path: '/submissions',
    title: 'Was aus deinen Berichten wird',
    body:
      'Unter „Submissions" liegen deine eigenen Einreichungen — abgeschickte und geparkte ' +
      'Service-Reports und Lieferscheine. Das „Einsatz-Archiv" zeigt die abgeschlossenen ' +
      'Vorgänge im Rückblick.',
    steps: [
      'Geparkte Entwürfe hier fortsetzen, statt neu anzufangen.',
      'Versehentlich gelöscht? Der Papierkorb hält es eine Weile vor.',
    ],
  },
  {
    id: 'kian',
    path: '/',
    title: 'KIAN — der Assistent unten rechts',
    body:
      'Der orange Knopf unten rechts öffnet den Assistenten. Er kennt das Portal, deine Tickets ' +
      'und die Kundendaten und beantwortet Fragen im Zusammenhang — „was war beim letzten ' +
      'Einsatz bei Firma X", „welches Gerät steht dort". Am Ticket findest du zusätzlich die ' +
      'KIAN-Analyse, die den Vorgang zusammenfasst.',
    caution:
      'KIAN antwortet auf Basis der vorhandenen Daten. Bei etwas, das du weitergibst, lohnt ' +
      'der prüfende Blick — er ersetzt deine Einschätzung nicht.',
  },
  {
    id: 'profile',
    path: '/profile',
    title: 'Dein Profil',
    body:
      'Zwei Dinge lohnen sich hier einmalig: die Heimatadresse und dein Standard-Startort. ' +
      'Beides bestimmt, wie deine Anfahrt berechnet wird.',
    steps: [
      'Heimatadresse eintragen — sie wird von einem Administrator bestätigt und gilt dann.',
      'Standard-Startort wählen: von zuhause oder vom Büro aus.',
      'Rufnummern hinterlegen, damit eingehende Anrufe dir zugeordnet werden.',
    ],
    caution:
      'Bis die Adresse bestätigt ist, rechnet die Anfahrt weiter mit dem alten Stand — ' +
      'so verschieben sich laufende Abrechnungen nicht rückwirkend.',
  },
  {
    id: 'help',
    path: '/help/faq',
    title: 'Wo du das hier wiederfindest',
    body:
      'Diese Seite ist dein Nachschlagewerk. Ganz oben steht der Rundgang — von hier aus ' +
      'kannst du ihn jederzeit neu starten, so oft du willst. Darunter liegt die FAQ mit ' +
      'kurzen Antworten zu allen Bereichen, durchsuchbar.',
    steps: [
      'Rundgang wiederholen: „Starten" in der Karte oben auf dieser Seite.',
      'Schneller Weg hierher: Strg+K, „Hilfe" tippen, Enter.',
      'Konkrete Frage? Der KIAN-Chat unten rechts kennt das Portal und deine Tickets.',
    ],
    caution:
      'Der Hinweis auf dem Dashboard verschwindet, sobald du ihn wegklickst oder den ' +
      'Rundgang beendest — diese Seite hier bleibt.',
  },
  {
    id: 'admin-requests',
    path: '/admin/technician-requests',
    title: 'Freigaben (nur Admin)',
    adminOnly: true,
    body:
      'Alle Materialanfragen laufen hier auf. An jeder offenen Anfrage steht eine Einordnung: ' +
      '„Routine" heißt, der Betrag liegt im Monatsrichtwert und nichts fällt auf — in der Regel ' +
      'ein Klick. „Prüfen" nennt den Grund dazu.',
    caution:
      'Auch interne Anfragen brauchen jetzt eine Freigabe. Vorher waren sie sofort genehmigt und ' +
      'niemand wurde benachrichtigt.',
  },
  {
    id: 'admin-users',
    path: '/admin/users',
    title: 'Benutzer & Heimatadressen (nur Admin)',
    adminOnly: true,
    body:
      'Neben der Benutzerverwaltung liegen hier die Heimatadress-Änderungen zur Bestätigung. ' +
      'Sie erscheinen nur, wenn etwas offen ist, und zeigen den Vorher-Nachher-Vergleich.',
  },
];
